import { computed } from 'vue';
import { useMenuLinkStore } from '../stores/menuLinkStore';
import type { MenuLinkModel } from '../types/menuTypes';
import { LinkTypeEnum } from '../constants/menuConstants';

/**
 * Composable for consuming the link store and providing UI helpers
 *
 * Architecture: Services → Store → Composables → Components
 * - Le Store est la source de vérité (appelle directement les services)
 * - Le Composable expose le store et ajoute de la logique UI réutilisable
 */
export function useLinkApi() {
  const store = useMenuLinkStore();

  // Computed properties for UI
  const sortedLinks = computed(() => [...store.links].sort((a, b) => a.order - b.order));
  const wardLinks = computed(() => store.getLinksByWardLinkType(LinkTypeEnum.Ward));

  const hasLinks = computed(() => store.links.length > 0);

  const isLoading = computed(() => store.loading);

  const hasError = computed(() => store.error !== null);

  // Helper method for updating links order (wraps store method)
  async function updateLinksOrder(
    updatedLinks: MenuLinkModel[],
    originalLinks: MenuLinkModel[]
  ): Promise<void> {
    await store.updateLinksOrder(updatedLinks, originalLinks);
  }

  // Helper to get links by module ID
  function getLinksByModuleId(moduleId: number): MenuLinkModel[] {
    return store.getLinksByModuleId(moduleId);
  }

  // Helper to get sorted links by module ID
  function getSortedLinksByModuleId(moduleId: number): MenuLinkModel[] {
    return store.getLinksByModuleId(moduleId).sort((a, b) => a.order - b.order);
  }

  // Helper to get a link by ID
  function getLinkById(id: number): MenuLinkModel | undefined {
    return store.getLinkById(id);
  }

  // Helper to check if a link exists
  function linkExists(id: number): boolean {
    return store.links.some((l) => l.id === id);
  }

  // Helper to count links in a module
  function getLinksCountByModuleId(moduleId: number): number {
    return store.getLinksByModuleId(moduleId).length;
  }

  return {
    // Store actions (delegate to store)
    fetchLinks: store.fetchLinks,
    createLink: store.createLink,
    updateLink: store.updateLink,
    deleteLink: store.deleteLink,
    updateLinksOrder,

    // Store state (reactive)
    links: computed(() => store.links),
    loading: isLoading,
    error: computed(() => store.error),

    // UI helpers
    sortedLinks,
    hasLinks,
    hasError,
    getLinksByModuleId,
    getSortedLinksByModuleId,
    getLinkById,
    linkExists,
    getLinksCountByModuleId,
    wardLinks,

    // State management (delegate to store)
    setLinks: store.setLinks,
    clearError: store.clearError,
  };
}
